import React, { memo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { WorkflowItem } from '../../config';
import IconButton from '../../../../components/IconButton';
import { MoreIcon } from '../../../../components/IconButton/icons';
import KanbanCardBody from '../KanbanCardBody';
import './kanban-card.scss';

// =======================================
// INTERFACES
// =======================================

interface KanbanCardProps {
  item: WorkflowItem;
  onClick: (item: WorkflowItem) => void;
  /** Opens the Move to sheet. Absent for a viewer who cannot move cards. */
  onMoveRequest?: (item: WorkflowItem) => void;
}

// A press on the Move to button must not arm a drag: dnd-kit's sensors listen
// for mousedown and touchstart on the card, so both stop at the button.
const stopDragStart = (e: React.SyntheticEvent) => e.stopPropagation();

// =======================================
// MAIN COMPONENT
// =======================================

/**
 * A card in a column. Draggable only -- a card can change column but has no
 * position within one (the column's sort decides), so there is no sortable
 * shuffling while a card is held. The card left behind while dragging is a
 * placeholder; the DragOverlay draws the lifted copy.
 *
 * The card is a plain drag surface, not a button: a click anywhere on it
 * opens the project (for a mouse or a finger), while the keyboard and screen
 * readers reach the title button, which opens it the same way, and the Move to
 * button beside it. dnd-kit's `attributes` are not spread -- they would make
 * the card a focusable role=button around those two real buttons, and mark it
 * aria-disabled for a read-only viewer who can still open it.
 */
const KanbanCard: React.FC<KanbanCardProps> = memo(({ item, onClick, onMoveRequest }) => {
  const canMove = Boolean(onMoveRequest);
  const { listeners, setNodeRef, isDragging } = useDraggable({
    id: `${item.entity_type}-${item.id}`,
    data: { item },
    disabled: !canMove,
  });

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={setNodeRef}
      {...listeners}
      className={`kanban-card ${isDragging ? 'is-dragging' : ''}`.trim()}
      onClick={() => onClick(item)}
    >
      <KanbanCardBody
        item={item}
        openable
        actions={
          onMoveRequest && (
            <IconButton
              size="sm"
              className="kcard__more"
              icon={<MoreIcon />}
              label={`Move ${item.job_number} to…`}
              onMouseDown={stopDragStart}
              onTouchStart={stopDragStart}
              onClick={(e) => {
                e.stopPropagation();
                onMoveRequest(item);
              }}
            />
          )
        }
      />
    </div>
  );
});

KanbanCard.displayName = 'KanbanCard';

export default KanbanCard;
